import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import {
  Search,
  LayoutDashboard,
  Shield,
  ShieldCheck,
  Lock,
  ClipboardCheck,
  BarChart3,
  Database,
  Bot,
  Settings,
  AlertTriangle,
  Briefcase,
  Users,
  PlusCircle,
  FileText,
  ArrowRight,
  Clock,
  X,
  Pin,
  Star,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useKnowledgeSearch } from '@/services/copilot.service'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CommandItem {
  id: string
  label: string
  description?: string
  icon: React.ReactNode
  category: 'Pages' | 'Actions' | 'Entites'
  action: () => void
  keywords?: string[]
}

const RECENT_SEARCHES_KEY = 'sgrc-ac-command-palette-recent'
const MAX_RECENT = 5
const FAVORITES_KEY = 'sgrc-ac-command-palette-favorites'

// ---------------------------------------------------------------------------
// Context + provider
// ---------------------------------------------------------------------------

const CommandPaletteContext = React.createContext<{
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}>({ open: false, setOpen: () => {} })

export function useCommandPalette() {
  return React.useContext(CommandPaletteContext)
}

export function CommandPaletteProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false)
  return (
    <CommandPaletteContext.Provider value={{ open, setOpen }}>
      {children}
      <CommandPalette open={open} onOpenChange={setOpen} />
    </CommandPaletteContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Nav + action items (cabinet portal)
// ---------------------------------------------------------------------------

const ICON_CLS = 'h-4 w-4 shrink-0 text-muted-foreground'

function buildNavItems(navigate: (path: string) => void): CommandItem[] {
  return [
    { id: 'nav-dashboard', label: 'Tableau de bord', icon: <LayoutDashboard className={ICON_CLS} />, category: 'Pages', action: () => navigate('/dashboard'), keywords: ['accueil', 'home', 'dashboard'] },
    { id: 'nav-mandats', label: 'Mandats', icon: <Briefcase className={ICON_CLS} />, category: 'Pages', action: () => navigate('/mandats'), keywords: ['engagement', 'mission', 'mandat'] },
    { id: 'nav-clients', label: 'Clients', icon: <Users className={ICON_CLS} />, category: 'Pages', action: () => navigate('/clients'), keywords: ['client', 'portefeuille'] },
    { id: 'nav-governance-pro', label: 'GovernancePRO', icon: <Shield className={ICON_CLS} />, category: 'Pages', action: () => navigate('/governance-pro'), keywords: ['gouvernance', 'politiques'] },
    { id: 'nav-risk-pro', label: 'RiskPRO', icon: <AlertTriangle className={ICON_CLS} />, category: 'Pages', action: () => navigate('/risk-pro'), keywords: ['risque', 'registre'] },
    { id: 'nav-security-pro', label: 'SecurityPRO', icon: <Lock className={ICON_CLS} />, category: 'Pages', action: () => navigate('/security-pro'), keywords: ['securite', 'iso 27001'] },
    { id: 'nav-compliance-pro', label: 'CompliancePRO', icon: <ShieldCheck className={ICON_CLS} />, category: 'Pages', action: () => navigate('/compliance-pro'), keywords: ['conformite', 'rgpd', 'loi 25'] },
    { id: 'nav-audit-pro', label: 'AuditPRO', icon: <ClipboardCheck className={ICON_CLS} />, category: 'Pages', action: () => navigate('/audit-pro'), keywords: ['audit', 'mission', 'certification'] },
    { id: 'nav-data-pro', label: 'DataPRO', icon: <Database className={ICON_CLS} />, category: 'Pages', action: () => navigate('/data-pro'), keywords: ['donnees', 'data', 'mdm'] },
    { id: 'nav-ai-ia-pro', label: 'AI-IAGPRO', icon: <Bot className={ICON_CLS} />, category: 'Pages', action: () => navigate('/ai-ia-pro'), keywords: ['ia', 'intelligence artificielle', 'mlops'] },
    { id: 'nav-maturity-pro', label: 'MaturityPRO', icon: <BarChart3 className={ICON_CLS} />, category: 'Pages', action: () => navigate('/maturity-pro'), keywords: ['maturite', 'evaluation', 'cmmi'] },
    { id: 'nav-dashboard-pro', label: 'DashboardPRO', icon: <BarChart3 className={ICON_CLS} />, category: 'Pages', action: () => navigate('/dashboard-pro'), keywords: ['tableaux de bord', 'kpi', 'benchmark'] },
    { id: 'nav-settings', label: 'Paramètres', icon: <Settings className={ICON_CLS} />, category: 'Pages', action: () => navigate('/settings'), keywords: ['parametres', 'reglages'] },
  ]
}

function buildActionItems(navigate: (path: string) => void): CommandItem[] {
  return [
    { id: 'act-client', label: 'Nouveau client', icon: <PlusCircle className={ICON_CLS} />, category: 'Actions', action: () => navigate('/clients?create=1'), keywords: ['creer client', 'ajouter client'] },
    { id: 'act-mandat', label: 'Nouveau mandat', icon: <Briefcase className={ICON_CLS} />, category: 'Actions', action: () => navigate('/mandats?create=1'), keywords: ['creer mandat', 'engagement'] },
    { id: 'act-risk', label: 'Créer un risque', icon: <AlertTriangle className={ICON_CLS} />, category: 'Actions', action: () => navigate('/risk-pro/identify'), keywords: ['nouveau risque'] },
    { id: 'act-audit', label: 'Nouvelle mission audit', icon: <ClipboardCheck className={ICON_CLS} />, category: 'Actions', action: () => navigate('/audit-pro/plan-wizard'), keywords: ['mission', 'creer audit'] },
    { id: 'act-eval', label: 'Nouvelle évaluation', icon: <BarChart3 className={ICON_CLS} />, category: 'Actions', action: () => navigate('/maturity-pro/questionnaires'), keywords: ['evaluation', 'campagne'] },
    { id: 'act-policy', label: 'Nouvelle politique', icon: <FileText className={ICON_CLS} />, category: 'Actions', action: () => navigate('/governance-pro/policies'), keywords: ['policy', 'politique'] },
  ]
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function normalizeText(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

function fuzzyMatch(text: string, query: string): boolean {
  const lower = normalizeText(text)
  const q = normalizeText(query)
  let qi = 0
  for (let i = 0; i < lower.length && qi < q.length; i++) {
    if (lower[i] === q[qi]) qi++
  }
  return qi === q.length
}

function matchesItem(item: CommandItem, query: string): boolean {
  if (fuzzyMatch(item.label, query)) return true
  if (item.description && fuzzyMatch(item.description, query)) return true
  if (item.keywords?.some((kw) => fuzzyMatch(kw, query))) return true
  return false
}

function getRecentSearches(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    return []
  }
}

function addRecentSearch(query: string) {
  if (!query.trim()) return
  const recent = getRecentSearches().filter((s) => s !== query)
  recent.unshift(query)
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)))
}

function getFavorites(): string[] {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    return []
  }
}

function saveFavorites(ids: string[]) {
  try {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(ids))
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate()
  const inputRef = React.useRef<HTMLInputElement>(null)

  const [query, setQuery] = React.useState('')
  const [debouncedQuery, setDebouncedQuery] = React.useState('')
  const [activeIndex, setActiveIndex] = React.useState(0)
  const [recentSearches, setRecentSearches] = React.useState<string[]>([])
  const [favorites, setFavorites] = React.useState<string[]>([])

  const navItems = React.useMemo(() => buildNavItems(navigate), [navigate])
  const actionItems = React.useMemo(() => buildActionItems(navigate), [navigate])
  const allStatic = React.useMemo(() => [...navItems, ...actionItems], [navItems, actionItems])

  // Debounce for the semantic entity search.
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300)
    return () => clearTimeout(t)
  }, [query])

  const { data: entityHits = [], isFetching } = useKnowledgeSearch(
    debouncedQuery,
    undefined,
    open && debouncedQuery.length >= 2,
  )

  const toggleFavorite = React.useCallback((id: string) => {
    setFavorites((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [id, ...prev]
      saveFavorites(next)
      return next
    })
  }, [])

  const staticIdSet = React.useMemo(() => new Set(allStatic.map((s) => s.id)), [allStatic])

  const favoriteItems = React.useMemo(
    () =>
      favorites
        .map((id) => allStatic.find((it) => it.id === id))
        .filter((it): it is CommandItem => Boolean(it)),
    [favorites, allStatic],
  )

  const filteredStatic = React.useMemo(() => {
    if (!query.trim()) return allStatic
    return allStatic.filter((item) => matchesItem(item, query))
  }, [query, allStatic])

  const entityItems: CommandItem[] = React.useMemo(
    () =>
      entityHits.map((r) => ({
        id: `entity-${r.entity_type}-${r.entity_id}`,
        label: r.title || 'Sans titre',
        description: r.entity_type,
        icon: <ArrowRight className={ICON_CLS} />,
        category: 'Entites' as const,
        action: () => {
          if (r.url) navigate(r.url)
        },
        keywords: [],
      })),
    [entityHits, navigate],
  )

  const grouped = React.useMemo(() => {
    const pages = filteredStatic.filter((i) => i.category === 'Pages')
    const actions = filteredStatic.filter((i) => i.category === 'Actions')
    const groups: { label: string; items: CommandItem[] }[] = []
    if (pages.length) groups.push({ label: 'Pages', items: pages })
    if (actions.length) groups.push({ label: 'Actions', items: actions })
    if (entityItems.length) groups.push({ label: 'Résultats', items: entityItems })
    return groups
  }, [filteredStatic, entityItems])

  const flatItems = React.useMemo(() => grouped.flatMap((g) => g.items), [grouped])

  React.useEffect(() => {
    if (open) {
      setQuery('')
      setDebouncedQuery('')
      setActiveIndex(0)
      setRecentSearches(getRecentSearches())
      setFavorites(getFavorites())
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  React.useEffect(() => {
    if (activeIndex >= flatItems.length) setActiveIndex(Math.max(0, flatItems.length - 1))
  }, [flatItems.length, activeIndex])

  // Global Ctrl/⌘K toggle.
  React.useEffect(() => {
    function handleGlobalKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && (e.key ?? '').toLowerCase() === 'k') {
        e.preventDefault()
        onOpenChange(!open)
      }
    }
    document.addEventListener('keydown', handleGlobalKey)
    return () => document.removeEventListener('keydown', handleGlobalKey)
  }, [open, onOpenChange])

  function handleKeyDown(e: React.KeyboardEvent) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActiveIndex((prev) => (prev + 1) % Math.max(flatItems.length, 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveIndex((prev) => (prev - 1 + flatItems.length) % Math.max(flatItems.length, 1))
        break
      case 'Enter':
        e.preventDefault()
        if (flatItems[activeIndex]) selectItem(flatItems[activeIndex])
        break
      case 'Escape':
        e.preventDefault()
        onOpenChange(false)
        break
    }
  }

  function selectItem(item: CommandItem) {
    addRecentSearch(query || item.label)
    item.action()
    onOpenChange(false)
  }

  const listRef = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    const container = listRef.current
    if (!container) return
    const active = container.querySelector('[data-active="true"]')
    if (active) active.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  const showRecent = !query.trim() && recentSearches.length > 0
  const showFavorites = !query.trim() && favoriteItems.length > 0

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[20%] z-50 w-full max-w-xl translate-x-[-50%] rounded-xl border bg-popover shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          onKeyDown={handleKeyDown}
          aria-label="Command palette"
        >
          <DialogPrimitive.Title className="sr-only">Palette de commandes</DialogPrimitive.Title>

          {/* Search input */}
          <div className="flex items-center gap-2 border-b px-4 py-3">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setActiveIndex(0)
              }}
              placeholder="Rechercher pages, actions, entités..."
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              autoComplete="off"
              spellCheck={false}
            />
            {isFetching && (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
            )}
            <button
              onClick={() => onOpenChange(false)}
              className="rounded p-0.5 text-muted-foreground hover:text-foreground"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Shortcut hint */}
          <div className="flex items-center justify-between border-b px-4 py-1.5 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-2">
              <span className="flex items-center gap-1">
                <kbd className="rounded border bg-muted px-1 py-0.5 font-mono text-[10px]">&uarr;&darr;</kbd>
                naviguer
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border bg-muted px-1 py-0.5 font-mono text-[10px]">&crarr;</kbd>
                ouvrir
              </span>
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border bg-muted px-1 py-0.5 font-mono text-[10px]">?</kbd>
              tous les raccourcis
            </span>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-[360px] overflow-y-auto overscroll-contain py-1">
            {showFavorites && (
              <div className="px-2 py-1.5">
                <p className="flex items-center gap-1 px-2 pb-1 text-xs font-medium text-muted-foreground">
                  <Star className="h-3 w-3 fill-current text-amber-500" />
                  Favoris
                </p>
                {favoriteItems.map((item) => (
                  <div key={item.id} className="group/fav relative">
                    <button
                      onClick={() => selectItem(item)}
                      className="flex w-full items-center gap-3 rounded-md px-2 py-1.5 pr-8 text-sm text-foreground hover:bg-accent/50"
                    >
                      {item.icon}
                      <span className="flex-1 truncate text-left">{item.label}</span>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleFavorite(item.id)
                      }}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-amber-500 hover:text-foreground"
                      title="Retirer des favoris"
                      aria-label="Retirer des favoris"
                    >
                      <Star className="h-3.5 w-3.5 fill-current" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {showRecent && (
              <div className="px-2 py-1.5">
                <p className="px-2 pb-1 text-xs font-medium text-muted-foreground">Recherches récentes</p>
                {recentSearches.map((recent) => (
                  <button
                    key={recent}
                    onClick={() => {
                      setQuery(recent)
                      setActiveIndex(0)
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-accent"
                  >
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    {recent}
                  </button>
                ))}
              </div>
            )}

            {grouped.length === 0 && query.trim() && !isFetching && (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                Aucun résultat pour &laquo;&nbsp;{query}&nbsp;&raquo;
              </p>
            )}

            {grouped.map((group) => {
              let startIdx = 0
              for (const g of grouped) {
                if (g === group) break
                startIdx += g.items.length
              }
              return (
                <div key={group.label} className="px-2 py-1.5">
                  <p className="px-2 pb-1 text-xs font-medium text-muted-foreground">{group.label}</p>
                  {group.items.map((item, i) => {
                    const flatIdx = startIdx + i
                    const isActive = flatIdx === activeIndex
                    const pinnable = staticIdSet.has(item.id)
                    const isFav = pinnable && favorites.includes(item.id)
                    return (
                      <div key={item.id} className="group/item relative">
                        <button
                          data-active={isActive}
                          onClick={() => selectItem(item)}
                          onMouseEnter={() => setActiveIndex(flatIdx)}
                          className={cn(
                            'flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm transition-colors',
                            pinnable && 'pr-8',
                            isActive ? 'bg-accent text-accent-foreground' : 'text-foreground hover:bg-accent/50',
                          )}
                        >
                          {item.icon}
                          <span className="flex-1 truncate text-left">{item.label}</span>
                          {item.description && (
                            <span className="shrink-0 text-xs text-muted-foreground">{item.description}</span>
                          )}
                          {isActive && !pinnable && (
                            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          )}
                        </button>
                        {pinnable && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleFavorite(item.id)
                            }}
                            className={cn(
                              'absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 transition-opacity',
                              isFav
                                ? 'text-amber-500 opacity-100'
                                : 'text-muted-foreground opacity-0 hover:text-foreground group-hover/item:opacity-100',
                            )}
                            title={isFav ? 'Retirer des favoris' : 'Épingler aux favoris'}
                            aria-label={isFav ? 'Retirer des favoris' : 'Épingler aux favoris'}
                          >
                            {isFav ? <Star className="h-3.5 w-3.5 fill-current" /> : <Pin className="h-3.5 w-3.5" />}
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
